import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { aiServiceAxios } from "../../utils/aiService"
import type {
  ApiResponse,
  BatchTriagePayload,
  MutationContext,
} from "./AlertCurationUtils"
import { ALL_DISEASES } from "./AlertCurationUtils"

export const getAlertCandidatesQueryKey = (
  params: UseAlertCandidatesParams
) => [
  "alertCandidates",
  1 - params.sensitivity,
  params.minScore,
  params.disease,
  params.search,
  params.startDate,
  params.endDate,
  params.sessionId,
]

export const useAlertDiseases = () => {
  return useQuery<string[]>({
    queryKey: ["alertDiseases"],
    queryFn: async (): Promise<string[]> => {
      const res = await aiServiceAxios.get<string[]>(
        "/api/v1/ai/candidates/diseases/"
      )
      return res.data
    },
  })
}

export const useAlertCandidatesLastUpdated = () => {
  return useQuery<string>({
    queryKey: ["alertCandidatesLastUpdated"],
    queryFn: async (): Promise<string> => {
      const res = await aiServiceAxios.get<{ last_updated: string }>(
        "/api/v1/ai/candidates/last_updated/"
      )
      return res.data.last_updated
    },
  })
}

interface UseAlertCandidatesParams {
  sensitivity: number
  minScore: number
  disease: string
  search?: string
  startDate: string
  endDate: string
  sessionId: string
}

export const useAlertCandidates = (params: UseAlertCandidatesParams) => {
  const queryKey = getAlertCandidatesQueryKey(params)

  return useQuery<ApiResponse>({
    queryKey,
    queryFn: async (): Promise<ApiResponse> => {
      const res = await aiServiceAxios.get<ApiResponse>(
        "/api/v1/ai/candidates/",
        {
          params: {
            session_id: params.sessionId,
            sensitivity: 1 - params.sensitivity,
            min_score: params.minScore,
            disease:
              params.disease === ALL_DISEASES ? undefined : params.disease,
            search: params.search || undefined,
            start_date: params.startDate,
            end_date: params.endDate,
          },
        }
      )
      return {
        count: res.data.count,
        results: res.data.results,
      } as ApiResponse
    },
  })
}

export const useTriageMutation = (params: UseAlertCandidatesParams) => {
  const queryClient = useQueryClient()
  const queryKey = getAlertCandidatesQueryKey(params)

  return useMutation<
    { status: string; count: number },
    Error,
    BatchTriagePayload,
    MutationContext
  >({
    mutationFn: async (payload: BatchTriagePayload) => {
      const res = await aiServiceAxios.post<{ status: string; count: number }>(
        "/api/v1/ai/candidates/batch_triage/",
        payload
      )
      return res.data
    },
    onMutate: async (payload: BatchTriagePayload) => {
      await queryClient.cancelQueries({ queryKey })
      const previousData = queryClient.getQueryData<ApiResponse>(queryKey)

      if (previousData) {
        queryClient.setQueryData<ApiResponse>(queryKey, {
          ...previousData,
          results: previousData.results.map(item =>
            payload.ids.includes(item.id)
              ? { ...item, is_archived: payload.action === "archive" }
              : item
          ),
        })
      }
      return { previousData }
    },
    onError: (_err, _newTodo, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData)
      }
    },
  })
}
